import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Send, Loader2, Building2, MapPin, Users, Check, Search, AlertTriangle, Megaphone } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function MultiSelect({ label, icon: Icon, items, selected, onToggle, getLabel, getSub, testid, emptyText, disabled }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((it) => `${getLabel(it)} ${getSub ? getSub(it) : ""}`.toLowerCase().includes(s));
  }, [items, q, getLabel, getSub]);

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm flex flex-col" data-testid={testid}>
      <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-slate-400" />
          <h3 className="text-sm font-bold text-slate-900">{label}</h3>
        </div>
        <span className="text-xs font-semibold text-slate-500 bg-slate-50 border border-slate-200 rounded-full px-2.5 py-0.5" data-testid={`${testid}-count`}>
          {selected.length} selected
        </span>
      </div>
      <div className="p-3 border-b border-slate-50">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <input
            data-testid={`${testid}-search`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={`Search ${label.toLowerCase()}`}
            disabled={disabled}
            className="h-9 w-full rounded-lg border border-slate-200 pl-8 pr-2 text-xs outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 disabled:bg-slate-50"
          />
        </div>
      </div>
      <div className="max-h-64 overflow-y-auto p-2 space-y-1 flex-1">
        {disabled ? (
          <p className="text-xs text-slate-400 text-center py-8">{emptyText}</p>
        ) : filtered.length === 0 ? (
          <p className="text-xs text-slate-400 text-center py-8">No results</p>
        ) : (
          filtered.map((it) => {
            const on = selected.includes(it.id);
            return (
              <button
                key={it.id}
                type="button"
                data-testid={`${testid}-item-${it.id}`}
                onClick={() => onToggle(it.id)}
                className={`w-full flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-left transition ${on ? "bg-indigo-50" : "hover:bg-slate-50"}`}
              >
                <span className={`grid place-items-center h-4 w-4 rounded border flex-shrink-0 ${on ? "bg-indigo-600 border-indigo-600" : "border-slate-300 bg-white"}`}>
                  {on && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                </span>
                <span className="min-w-0">
                  <span className="block text-xs font-semibold text-slate-800 truncate">{getLabel(it)}</span>
                  {getSub && getSub(it) && <span className="block text-[11px] text-slate-400 truncate">{getSub(it)}</span>}
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

export default function PushBroadcast() {
  const [companyIds, setCompanyIds] = useState([]);
  const [siteIds, setSiteIds] = useState([]);
  const [userIds, setUserIds] = useState([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending, setSending] = useState(false);

  const { data: companies = [] } = useQuery({
    queryKey: ["push-companies"],
    queryFn: () => api.get("/admin/businesses"),
  });

  const companyParam = companyIds.join(",");
  const { data: sites = [] } = useQuery({
    queryKey: ["push-sites", companyParam],
    queryFn: () => api.get(`/admin/push/sites?company_ids=${encodeURIComponent(companyParam)}`),
  });

  const siteParam = siteIds.join(",");
  const { data: users = [] } = useQuery({
    queryKey: ["push-users", companyParam, siteParam],
    queryFn: () =>
      api.get(`/admin/push/users?company_ids=${encodeURIComponent(companyParam)}&site_ids=${encodeURIComponent(siteParam)}`),
  });

  // Drop selected sites/users that are no longer in the filtered pool.
  useEffect(() => {
    const valid = new Set(sites.map((s) => s.id));
    setSiteIds((prev) => prev.filter((id) => valid.has(id)));
  }, [sites]);
  useEffect(() => {
    const valid = new Set(users.map((u) => u.id));
    setUserIds((prev) => prev.filter((id) => valid.has(id)));
  }, [users]);

  const toggle = (setter) => (id) =>
    setter((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  // Recipients = explicitly selected users, else the whole filtered pool.
  const recipientCount = userIds.length > 0 ? userIds.length : users.length;
  const canSend = title.trim() && body.trim() && recipientCount > 0;

  const doSend = async () => {
    setSending(true);
    try {
      const res = await api.post("/admin/push/broadcast", {
        title: title.trim(),
        body: body.trim(),
        company_ids: companyIds,
        site_ids: siteIds,
        user_ids: userIds,
      });
      if (res.push_ready === false) {
        toast.warning(res.message || "Push not configured — nothing was sent.");
      } else {
        toast.success(`Push sent to ${res.sent_count ?? res.recipient_count} user(s)`);
      }
      setTitle("");
      setBody("");
      setUserIds([]);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSending(false);
      setConfirmOpen(false);
    }
  };

  return (
    <div className="space-y-5 animate-fade-up" data-testid="push-broadcast-page">
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 flex items-center gap-3">
        <span className="grid place-items-center h-11 w-11 rounded-xl bg-indigo-50 text-indigo-600"><Megaphone className="h-5 w-5" /></span>
        <div>
          <h2 className="font-head text-xl font-bold text-slate-900">Push Broadcast</h2>
          <p className="text-sm text-slate-500">Send a push notification to staff on the mobile Attendance App.</p>
        </div>
      </div>

      {/* Targeting */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <MultiSelect
          label="Companies" icon={Building2} testid="ms-companies"
          items={companies} selected={companyIds} onToggle={toggle(setCompanyIds)}
          getLabel={(c) => c.name} getSub={(c) => `${c.employee_count ?? 0} staff`}
        />
        <MultiSelect
          label="Sites" icon={MapPin} testid="ms-sites"
          items={sites} selected={siteIds} onToggle={toggle(setSiteIds)}
          getLabel={(s) => s.name}
        />
        <MultiSelect
          label="Users" icon={Users} testid="ms-users"
          items={users} selected={userIds} onToggle={toggle(setUserIds)}
          getLabel={(u) => u.name || u.staff_id}
          getSub={(u) => [u.staff_id, u.site_name].filter(Boolean).join(" · ")}
        />
      </div>

      <p className="text-xs text-slate-400">
        Tip: leave <b>Users</b> unselected to send to <b>everyone</b> in the filtered pool. Sites are filtered by the selected companies.
      </p>

      {/* Composer */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 space-y-4" data-testid="push-composer">
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1.5">Title</label>
          <input
            data-testid="push-title-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={120}
            placeholder="e.g. Reminder"
            className="h-11 w-full rounded-lg border border-slate-200 px-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold uppercase tracking-wide text-slate-400 mb-1.5">Body</label>
          <textarea
            data-testid="push-body-input"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            maxLength={2000}
            rows={4}
            placeholder="Type the message staff will receive…"
            className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 resize-y"
          />
        </div>
      </div>

      {/* Send bar */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-sm text-slate-600" data-testid="push-recipient-summary">
          This will be sent to <b className="text-slate-900" data-testid="push-recipient-count">{recipientCount}</b> user{recipientCount === 1 ? "" : "s"}
          {userIds.length === 0 && recipientCount > 0 ? " (everyone in the filtered pool)" : ""}.
        </p>
        <button
          data-testid="push-send-btn"
          disabled={!canSend || sending}
          onClick={() => setConfirmOpen(true)}
          className="inline-flex items-center justify-center gap-2 h-11 px-6 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 transition"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          Send Broadcast
        </button>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="push-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" /> Confirm broadcast
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-slate-600">
                <p>
                  This will send a push notification to <b className="text-slate-900">{recipientCount} user{recipientCount === 1 ? "" : "s"}</b> on the mobile Attendance App.
                </p>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                  <p className="font-semibold text-slate-800">{title || "(no title)"}</p>
                  <p className="text-slate-600 mt-1 whitespace-pre-wrap">{body || "(no body)"}</p>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="push-confirm-cancel" disabled={sending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="push-confirm-send"
              disabled={sending}
              onClick={(e) => { e.preventDefault(); doSend(); }}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : `Send to ${recipientCount}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
